import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import Popup from '../../../../components/ui/popup'
import cls from './likeList.module.sass'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../../../store/rootReducer'
import { isUserPro } from '../../../../utils/isUserPro'

import styles from '../post/Post.module.sass'
import Button from '../../../../components/ui/button'
import paths from '../../../../paths'
import { openMenu, setPageToReturnFromPayment } from '../../../../store/common/commonSlice'
import { Link, useHistory, useLocation } from 'react-router-dom'
import { getEphemeralListLikes } from '../../../../api/ephemeral'
import PaginationLoader from '../../../../components/ui/paginationLoader/PaginationLoader'
import { createLocalizedPath } from '../../../../utils/localizationHelpers'

interface Props {
    onClose: () => void
}

const LikeList = ({ onClose }: Props) => {
    const [t] = useTranslation()
    const dispatch = useDispatch()

    const location = useLocation()
    const history = useHistory()

    const id = Number(new URLSearchParams(location.search).get('likes'))
    const user = useSelector((state: RootState) => state.member.user)
    const loggedIn = useSelector((state: RootState) => state.member.loggedIn)

    const [currentPage, setCurrentPage] = React.useState(0)
    const limit = 32
    const [likeList, setLikeList] = React.useState([])
    const [hasMore, setHasMore] = React.useState(true)
    const [isLoading, setIsLoading] = React.useState(false)

    useEffect(() => {
        setLikeList([])
        setCurrentPage(0)
    }, [location.search])

    useEffect(() => {
        const fetchLikeList = async () => {
            setIsLoading(true)
            try {
                const fetchLikeListResponse = await getEphemeralListLikes({
                    id,
                    limit,
                    offset: currentPage * limit
                })
                if (!fetchLikeListResponse.list || fetchLikeListResponse.list.length < limit) {
                    setHasMore(false)
                }
                if (fetchLikeListResponse.status === 'success')
                    setLikeList((prev) =>
                        currentPage === 0
                            ? fetchLikeListResponse.list || []
                            : [...prev, ...(fetchLikeListResponse.list || [])]
                    )
            } catch (error) {
                setHasMore(false)
                console.error('Error fetching like list:', error)
            } finally {
                setIsLoading(false)
            }
        }

        if (id !== 0 && (hasMore || currentPage === 0)) {
            fetchLikeList()
        }
    }, [currentPage, id])

    const loadNextPage = () => {
        if (hasMore) {
            setCurrentPage((prev) => prev + 1)
        }
    }

    const resetPagination = () => {
        setCurrentPage(0)
        setHasMore(true)
    }

    return (
        <Popup title={t('Like list')} onClose={onClose} classname={cls['popup']}>
            <div className={cls['likeList']}>
                {user && loggedIn && !isUserPro(user.member_type) && likeList.length === 0 && (
                    <div className={styles['paymentDialog']}>
                        <p className="menuHeader">{t('You need to become Premium member to use this feature')}</p>
                        <div className={styles['buttonLine']}>
                            <Button
                                label={t('Become Premium')}
                                // @ts-ignore
                                onClick={(e: { stopPropagation: () => void }) => {
                                    e.stopPropagation()
                                    history.push(paths.payment.path)
                                    dispatch(setPageToReturnFromPayment(location.pathname))
                                }}
                            />
                        </div>
                    </div>
                )}
                {!user && !loggedIn && (
                    <div className={styles['paymentDialog']}>
                        <p className="menuHeader">{t('You need to be authorized to use this feature')}</p>
                        <div className={styles['buttonLine']}>
                            <Button
                                label={'Log in'}
                                // @ts-ignore
                                onClick={(e) => {
                                    e.stopPropagation()
                                    dispatch(openMenu())
                                }}
                            />
                        </div>
                    </div>
                )}
                {((user && loggedIn && isUserPro(user.member_type)) || likeList.length > 0) && (
                    <>
                        {likeList.map((like) => (
                            <div className={cls['avatar_container']} key={like.id}>
                                <div className={cls['avatar']}>
                                    <img
                                        src={
                                            like.user.avatar_url
                                                ? like.user.avatar_url
                                                : require('../../../../assets/404.png')
                                        }
                                        onClick={() => {
                                            history.push(
                                                createLocalizedPath(paths.profile.toPath({ nick: like.user.nick }))
                                            )
                                        }}
                                    />
                                </div>
                                <Link to={paths.profile.toPath({ nick: like.user.nick })}>{like.user.nick}</Link>
                            </div>
                        ))}
                    </>
                )}
            </div>
            {hasMore &&
                (isLoading ? (
                    <div
                        style={{
                            width: '100%',
                            display: 'flex',
                            justifyContent: 'center'
                        }}
                    >
                        <PaginationLoader />
                    </div>
                ) : (
                    <div
                        style={{
                            marginLeft: '40px',
                            color: 'var(--main-theme-color)',
                            cursor: 'pointer',
                            fontWeight: 'bold',
                            textAlign: 'center',
                            marginTop: '16px'
                        }}
                        onClick={loadNextPage}
                    >
                        {t('Load more')}
                    </div>
                ))}
        </Popup>
    )
}

export default LikeList
